/**
 * ADR-0249 S5 — campaign auto-block section rendered inside
 * CampaignDetail. One primary action ("Auto-block this campaign"),
 * one confirm dialog, and a compact armed-state card with the Undo
 * button + joiner counter.
 *
 * Sill voice: no exclamation points, no "amazing", honest copy about
 * what the action does and does NOT do (protected paths still stop
 * the campaign on any address, verified crawlers never get blocked).
 *
 * Install-surface honesty: on a site whose `server_source` is plugin
 * (WP plugin without a Cloudflare Worker), the section renders a note
 * that IP blocks are Worker-only today. The action still proceeds —
 * blocks are inserted so the Worker-wire enables straight through,
 * and the audit trail is identical.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { AlertTriangle, Loader2, Shield, ShieldOff } from 'lucide-react';

import { useOptionalHttp } from '../../api/http-context.js';
import {
  disableCampaignAutoblock,
  enableCampaignAutoblock,
  getCampaignAutoblockState,
} from '../../api/threats.js';
import { useC } from '../../lib/theme.js';
import type {
  CampaignAutoblockDisableResponse,
  CampaignAutoblockEnableResponse,
  CampaignAutoblockState,
  CampaignAutoblockStateResponse,
  ThreatCampaignSummary,
} from '@sill/types/api';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface CampaignAutoBlockSectionProps {
  readonly siteId: string;
  readonly siteDomain: string;
  readonly campaign: ThreatCampaignSummary;
  /**
   * Current member count visible on the detail panel. Shown in the
   * confirm dialog so the merchant sees what will be blocked up-front.
   */
  readonly currentMemberCount: number;
  /** Fires after auto-block is turned on or undone, so the list and BLOCKED count refresh. */
  readonly onChanged?: () => void;
}

type ArmedState = CampaignAutoblockState;

type ViewState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'ready'; readonly data: CampaignAutoblockStateResponse };

interface ActionResult {
  readonly tone: 'ok' | 'warn' | 'err';
  readonly message: string;
}

function skipLine(reason: string, n: number): string | null {
  if (n <= 0) return null;
  switch (reason) {
    case 'verified_crawler':
      return `${n} address${n === 1 ? '' : 'es'} skipped — verified crawler`;
    case 'verified_crawler_snapshot_unavailable':
      return `${n} skipped — verified-crawler check unavailable, will retry`;
    case 'allowlisted':
      return `${n} address${n === 1 ? '' : 'es'} skipped — in your trusted addresses`;
    case 'self_dashboard':
      return `${n} address${n === 1 ? '' : 'es'} skipped — your own dashboard address`;
    case 'own_server':
      return `${n} address${n === 1 ? '' : 'es'} skipped — your own server (found in your DNS)`;
    case 'network_not_hosting':
      return `${n} waiting — not yet confirmed as a hosting or cloud network (Sill retries every minute)`;
    case 'safety_check_unavailable':
      return `${n} skipped — safety check unavailable, will retry`;
    case 'cap_reached':
      return `${n} address${n === 1 ? '' : 'es'} skipped — 1,000-block cap reached`;
    case 'invalid_ip':
      return `${n} address${n === 1 ? '' : 'es'} skipped — malformed address`;
    case 'already_blocked':
      return `${n} address${n === 1 ? '' : 'es'} already blocked`;
    default:
      return null;
  }
}

/** Format an ISO date into a "Oct 25" style the compact chip can read. */
function formatShortDate(iso: string | null): string | null {
  if (iso === null) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const m = d.toLocaleDateString('en-US', { month: 'short' });
  return `${m} ${d.getUTCDate()}`;
}

/** "Oct 19, 2:14 PM" in the viewer's time zone — when the last address
 *  joined the block (founder 2026-10-19: show the minute, not just the day). */
export function formatLastBlocked(iso: string | null): string | null {
  if (iso === null) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(d);
}

/** 7-day expiry for the confirm dialog. */
function sevenDaysFromNow(): string {
  const d = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
  return formatShortDate(d.toISOString()) ?? 'in 7 days';
}

interface SectionCardProps {
  readonly tone: 'default' | 'armed';
  readonly children: ReactNode;
}

function SectionCard({ tone, children }: SectionCardProps): JSX.Element {
  const C = useC();
  const border = tone === 'armed' ? C.limeFg : C.border;
  const bg = tone === 'armed' ? C.limeDim : C.surface2;
  const style: CSSProperties = {
    marginTop: 20,
    padding: 14,
    border: `1px solid ${border}`,
    background: bg,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  };
  return <div style={style}>{children}</div>;
}

interface BannerProps {
  readonly result: ActionResult | null;
}

function Banner({ result }: BannerProps): JSX.Element | null {
  const C = useC();
  if (result === null) return null;
  const colour =
    result.tone === 'ok' ? C.limeFg
    : result.tone === 'warn' ? C.amberFg
    : C.coralFg;
  const bg =
    result.tone === 'ok' ? C.limeDim
    : result.tone === 'warn' ? C.amberDim
    : C.coralDim;
  return (
    <div
      role="status"
      data-testid="campaign-autoblock-banner"
      style={{
        fontFamily: sansFont,
        fontSize: 12,
        color: colour,
        padding: '8px 10px',
        border: `1px solid ${colour}`,
        background: bg,
      }}
    >
      {result.message}
    </div>
  );
}

export function CampaignAutoBlockSection(
  { siteId, siteDomain, campaign, currentMemberCount, onChanged }: CampaignAutoBlockSectionProps,
): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const [view, setView] = useState<ViewState>({ kind: 'loading' });
  const [pending, setPending] = useState<'idle' | 'arming' | 'undoing'>('idle');
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [actionResult, setActionResult] = useState<ActionResult | null>(null);

  const reload = useCallback(async () => {
    if (http === null) {
      setView({ kind: 'error', message: 'The dashboard is offline.' });
      return;
    }
    setView({ kind: 'loading' });
    try {
      const res = await getCampaignAutoblockState(http, siteId, campaign.campaign_id);
      if (res === null) {
        setView({ kind: 'error', message: 'This campaign is no longer visible.' });
        return;
      }
      setView({ kind: 'ready', data: res });
    } catch {
      setView({ kind: 'error', message: 'Could not load the auto-block state.' });
    }
  }, [http, siteId, campaign.campaign_id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onArm = useCallback(async () => {
    if (http === null) return;
    setPending('arming');
    setActionResult(null);
    try {
      const res = await enableCampaignAutoblock(http, siteId, campaign.campaign_id);
      if (res.ok !== true) {
        setActionResult({
          tone: 'err',
          message: res.error === 'plan_feature_required'
            // ADR-0263 S4 — plain-copy message carries the plan name.
            ? (res.plan_message ?? (res.plan_name !== null && res.plan_name !== undefined
              ? `Blocking is part of the ${res.plan_name} plan.`
              : "Blocking is not on this site's plan."))
            : res.error === 'forbidden'
              ? 'Your role does not allow blocking.'
              : res.error === 'rate_limited'
                ? 'Too many requests. Try again in a minute.'
                : 'Could not arm auto-block. Try again.',
        });
      } else {
        const ok = res as CampaignAutoblockEnableResponse;
        const parts: string[] = [];
        parts.push(
          ok.blocked_count === 0
            ? 'Auto-block is on. No addresses blocked yet.'
            : `Auto-block is on. ${ok.blocked_count} address${ok.blocked_count === 1 ? '' : 'es'} blocked.`,
        );
        for (const [reason, n] of Object.entries(ok.skipped_by_reason)) {
          const line = skipLine(reason, n);
          if (line !== null) parts.push(line);
        }
        setActionResult({ tone: ok.cap_reached ? 'warn' : 'ok', message: parts.join(' · ') });
      }
      await reload();
      onChanged?.();
    } catch {
      setActionResult({ tone: 'err', message: 'Network error. Try again.' });
    } finally {
      setPending('idle');
      setConfirmOpen(false);
    }
  }, [http, siteId, campaign.campaign_id, reload]);

  const onUndo = useCallback(async () => {
    if (http === null) return;
    setPending('undoing');
    setActionResult(null);
    try {
      const res = await disableCampaignAutoblock(http, siteId, campaign.campaign_id);
      if (res.ok !== true) {
        setActionResult({
          tone: 'err',
          message: res.error === 'forbidden'
            ? 'Your role does not allow unblocking.'
            : 'Could not undo. Try again.',
        });
      } else {
        const ok = res as CampaignAutoblockDisableResponse;
        setActionResult({
          tone: 'ok',
          message: ok.revoked_count === 0
            ? 'Auto-block disarmed. No live entries to remove.'
            : `Auto-block disarmed. ${ok.revoked_count} address${ok.revoked_count === 1 ? '' : 'es'} unblocked.`,
        });
      }
      await reload();
      onChanged?.();
    } catch {
      setActionResult({ tone: 'err', message: 'Network error. Try again.' });
    } finally {
      setPending('idle');
    }
  }, [http, siteId, campaign.campaign_id, reload]);

  const armedState = useMemo<ArmedState | null>(() => {
    if (view.kind !== 'ready') return null;
    return view.data.state.armed ? view.data.state : null;
  }, [view]);

  if (view.kind === 'loading') {
    return (
      <SectionCard tone="default">
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
          Loading auto-block state.
        </div>
      </SectionCard>
    );
  }
  if (view.kind === 'error') {
    return (
      <SectionCard tone="default">
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
          {view.message}
        </div>
      </SectionCard>
    );
  }

  const { state, may_mutate, server_source } = view.data;
  const pluginOnly = server_source === 'plugin';
  const sevenDays = sevenDaysFromNow();

  // ARMED view ---------------------------------------------------------
  if (armedState !== null) {
    const expiryHint = formatLastBlocked(armedState.last_joiner_at ?? armedState.armed_at);
    return (
      <SectionCard tone="armed">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Shield size={14} color={C.limeFg} aria-hidden />
          <div
            data-testid="campaign-autoblock-armed-headline"
            style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500 }}
          >
            Auto-blocking · {armedState.blocked_count} address{armedState.blocked_count === 1 ? '' : 'es'} blocked · new addresses blocked as they join
          </div>
        </div>
        <div data-testid="campaign-autoblock-last-blocked" style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 0.3 }}>
          {expiryHint !== null ? `LAST ADDRESS BLOCKED · ${expiryHint.toUpperCase()}` : 'ON'}
        </div>
        {armedState.cap_hit_at !== null ? (
          <div
            data-testid="campaign-autoblock-cap-notice"
            style={{ fontFamily: sansFont, fontSize: 12, color: C.amberFg, lineHeight: 1.5 }}
          >
            <AlertTriangle size={11} style={{ display: 'inline-block', verticalAlign: '-1px', marginRight: 4 }} />
            Some new senders could not be auto-blocked — your block cap is at 1,000.
          </div>
        ) : null}
        {pluginOnly ? (
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
            <AlertTriangle size={11} style={{ display: 'inline-block', verticalAlign: '-1px', marginRight: 4 }} />
            Your WordPress plugin install does not currently enforce IP blocks. Add the Cloudflare Worker to activate.
          </div>
        ) : null}
        <Banner result={actionResult} />
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            data-testid="campaign-autoblock-undo"
            onClick={onUndo}
            disabled={!may_mutate || pending !== 'idle'}
            style={{
              background: 'transparent',
              color: C.text,
              border: `1px solid ${C.border}`,
              padding: '8px 14px',
              fontFamily: sansFont,
              fontSize: 12,
              cursor: !may_mutate || pending !== 'idle' ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {pending === 'undoing' ? <Loader2 size={12} className="sill-spin" aria-hidden /> : <ShieldOff size={12} aria-hidden />}
            Undo (unblock these addresses)
          </button>
        </div>
        {may_mutate ? null : (
          <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted }}>
            Your role is read-only on this site.
          </div>
        )}
      </SectionCard>
    );
  }

  // IDLE view ---------------------------------------------------------
  return (
    <SectionCard tone="default">
      <div
        data-testid="campaign-autoblock-headline"
        style={{ fontFamily: sansFont, fontSize: 13, color: C.text, lineHeight: 1.5 }}
      >
        Auto-block blocks this campaign’s addresses for 7 days, and new ones as they join. It only
        blocks addresses Sill has confirmed are on hosting or cloud networks; the rest wait until
        that is confirmed.
      </div>
      {pluginOnly ? (
        <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
          <AlertTriangle size={11} style={{ display: 'inline-block', verticalAlign: '-1px', marginRight: 4 }} />
          IP blocks are enforced by the Cloudflare Worker only. Your WordPress plugin install does not currently enforce IP blocks.
        </div>
      ) : null}
      <Banner result={actionResult} />
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          type="button"
          data-testid="campaign-autoblock-arm"
          onClick={() => setConfirmOpen(true)}
          disabled={!may_mutate || pending !== 'idle'}
          style={{
            background: may_mutate ? C.lime : C.border,
            color: may_mutate ? C.ink : C.muted,
            border: 'none',
            padding: '10px 16px',
            fontFamily: sansFont,
            fontSize: 13,
            fontWeight: 500,
            cursor: !may_mutate || pending !== 'idle' ? 'not-allowed' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {pending === 'arming' ? <Loader2 size={12} className="sill-spin" aria-hidden /> : <Shield size={12} aria-hidden />}
          Auto-block this campaign
        </button>
        {!may_mutate ? (
          <span style={{ fontFamily: sansFont, fontSize: 11, color: C.muted }}>
            Your role is read-only on this site.
          </span>
        ) : null}
      </div>
      {state.disarmed_at !== null && !state.armed ? (
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>
          Previously disarmed · {formatShortDate(state.disarmed_at)?.toUpperCase() ?? ''}
        </div>
      ) : null}

      {/* Confirm dialog — inline, not a modal, so mobile menus can
          open upward naturally with DetailActionRow's menuPlacement. */}
      {confirmOpen ? (
        <div
          data-testid="campaign-autoblock-confirm"
          role="dialog"
          aria-label="Confirm auto-block"
          style={{
            marginTop: 6,
            padding: 12,
            border: `1px solid ${C.coralFg}`,
            background: C.coralDim,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, lineHeight: 1.5 }}>
            Block {currentMemberCount} address{currentMemberCount === 1 ? '' : 'es'} on <strong>{siteDomain}</strong> for 7 days
            (expires {sevenDays}), and block new senders as they join this campaign.
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.body, lineHeight: 1.5 }}>
            Blocking attack paths (Settings → Blocking → Attack paths) stops the campaign on any address. Verified crawlers (Google, OpenAI, Anthropic, Perplexity),
            your allowlisted addresses, your own dashboard address, and addresses not identified as hosting networks
            are never blocked.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              data-testid="campaign-autoblock-confirm-yes"
              onClick={onArm}
              disabled={pending !== 'idle'}
              style={{
                background: C.coralFg,
                color: C.paper,
                border: 'none',
                padding: '8px 14px',
                fontFamily: sansFont,
                fontSize: 12,
                fontWeight: 500,
                cursor: pending !== 'idle' ? 'not-allowed' : 'pointer',
              }}
            >
              {pending === 'arming' ? 'Arming…' : 'Block now'}
            </button>
            <button
              type="button"
              data-testid="campaign-autoblock-confirm-cancel"
              onClick={() => setConfirmOpen(false)}
              disabled={pending !== 'idle'}
              style={{
                background: 'transparent',
                color: C.text,
                border: `1px solid ${C.border}`,
                padding: '8px 14px',
                fontFamily: sansFont,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </SectionCard>
  );
}
